import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { courses as coursesData } from "../../assets/data/courses";
import { FiSearch } from "react-icons/fi";
import { FaStar, FaRegStar } from "react-icons/fa";
import "./CoursesPage.css";

import level1Icon from "../../assets/courses/icons/level_1.png";
import level2Icon from "../../assets/courses/icons/level_2.png";
import level3Icon from "../../assets/courses/icons/level_3.png";

import finanzasBanner from "../../assets/courses/banners/finanzas.jpg";
import criptoBanner from "../../assets/courses/banners/cripto.jpg";

const courseFallbackBanners = {
  "finanzas-personales": finanzasBanner,
  "cripto-blockchain": criptoBanner,
};

export default function CoursesPage() {
  const { t, i18n } = useTranslation("courses");

  const currentLang = i18n.language.split("-")[0];
  const [search, setSearch] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return coursesData;

    return coursesData.filter((course) => {
      const courseTitle = t(`courses.${course.id}.title`, course.title);
      const levelLabel = t(`levels.${course.level}`, course.level);

      return (
        courseTitle.toLowerCase().includes(term) ||
        levelLabel.toLowerCase().includes(term) ||
        course.duration.toLowerCase().includes(term)
      );
    });
  }, [search, t]);

  return (
    <main className="courses-page">
      <section className="courses-hero">
        <div className="courses-hero__inner">
          <h1>
            {t("hero.titleBefore")} <span>{t("hero.titleHighlight")}</span>
          </h1>

          <p>{t("hero.description")}</p>
        </div>
      </section>

      <section className="courses-content">
        <div className="courses-search-wrapper">
          <FiSearch className="courses-search-icon" />

          <input
            className="courses-search"
            type="text"
            placeholder={t("searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <section className="courses-grid">
          {filtered.length > 0 ? (
            filtered.map((course) => {
              const courseTitle = t(`courses.${course.id}.title`, course.title);

              const courseDescription = t(
                `courses.${course.id}.description`,
                course.description,
              );

              const levelLabel = t(`levels.${course.level}`, course.level);

              const levelIcon = {
                Principiante: level1Icon,
                Intermedio: level2Icon,
                Avanzado: level3Icon,
              }[course.level];

              const banner =
                course.banners?.[currentLang] ||
                course.banners?.es ||
                Object.values(course.banners || {})[0] ||
                courseFallbackBanners[course.id];

              return (
                <Link
                  to={`/cursos/${course.id}`}
                  key={course.id}
                  className="course-card-link"
                >
                  <article className="course-card">
                    <div
                      className="course-card__image"
                      style={{ backgroundImage: `url(${banner})` }}
                    />

                    <div className="course-card__body">
                      <h2 className="course-card__title">{courseTitle}</h2>

                      <div className="course-card__rating">
                        {Array.from({ length: 5 }, (_, index) =>
                          index < Math.round(course.rating) ? (
                            <FaStar key={index} />
                          ) : (
                            <FaRegStar key={index} />
                          ),
                        )}

                        <span>({course.rating.toFixed(1)})</span>
                      </div>

                      <p className="course-card__description">
                        {courseDescription}
                      </p>

                      <div className="course-card__meta">
                        {levelIcon && (
                          <img
                            src={levelIcon}
                            alt={levelLabel}
                            className="course-card__level-icon"
                          />
                        )}

                        <span>{levelLabel}</span>
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })
          ) : (
            <div className="courses-no-results">
              <h2>{t("noResults.title")}</h2>
              <p>{t("noResults.description")}</p>
            </div>
          )}
        </section>
      </section>
    </main>
  );
}
